import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  capitalizeEntity,
  getFormattedStatsCount,
  handleErrorMessage,
  isTruthy,
} from "helpers/methods";
import { useState, useEffect, useContext } from "react";
import upgradePlanStyles from "./UpgradePlan.styles";
import { CustomSwitch } from "screens/Settings/Users/ViewUser";
import { getPlans } from "../PlanService";
import { useAppSelector } from "utils/hooks";
import { selectAccount } from "redux/authSlice";
import { MyPlanContext } from "../Context";
import { features } from "../AccountUsage/AccountUsage";

interface CustomProps {
  upgradePlan: boolean;
  setUpgradePlan: Function;
}

interface planLimits {
  entity: string;
  limit: number;
}

const UpgradePlan = (props: CustomProps) => {
  const classes = upgradePlanStyles;
  const { setUpgradePlan } = props;
  const { setSelectedPlan, featurePlanDetails } = useContext(MyPlanContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const accountName = useAppSelector(selectAccount);
  const [planCurrency, setPlanCurrency] = useState<string>("USD");
  const [planType, setPlanType] = useState<string>("monthly");
  const [allPlans, setAllPlans] = useState<any>();
  const [plans, setPlans] = useState<any>();
  const [isAnnualPlans, setIsAnnualPlans] = useState<boolean>(false);
  const activePlanId = featurePlanDetails.id;

  useEffect(() => {
    getAllPlans();
  }, []);

  const getAllPlans = async () => {
    try {
      setIsLoading(true);
      const response: any = await getPlans(accountName);
      const res = response.filter(
        (item: any) =>
          item?.metadata?.some((meta: any) => meta.name !== "isSms") &&
          item.price !== 0 &&
          item.status === "Active"
      );

      const filteredAnnualPlans = res.filter((item: any) =>
        item?.metadata?.some(
          (meta: any) =>
            meta.name === "subscriptionPlanType" && meta.value === planType
        )
      );

      setIsAnnualPlans(filteredAnnualPlans.length > 0 ? true : false);
      const filteredPlans =
        filteredAnnualPlans.length > 0 ? filteredAnnualPlans : res;

      const groupedData: any = {};
      filteredPlans?.forEach((item: any) => {
        if (!groupedData[item.currency]) {
          groupedData[item.currency] = [];
        }
        groupedData[item.currency].push(item);
      });
      setPlans(groupedData);
      setAllPlans(res);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handlePlanTypeToggle = (event: any) => {
    const newPlanType = event.target.checked ? "yearly" : "monthly";
    setPlanType(newPlanType);

    const filteredPlans = allPlans.filter((item: any) =>
      item?.metadata?.some(
        (meta: any) =>
          meta.name === "subscriptionPlanType" && meta.value === newPlanType
      )
    );

    const groupedData: any = {};
    filteredPlans.forEach((item: any) => {
      if (!groupedData[item.currency]) {
        groupedData[item.currency] = [];
      }
      groupedData[item.currency].push(item);
    });

    setPlans(groupedData);
    setPlanCurrency(isTruthy(groupedData) ? Object.keys(groupedData)[0] : "");
  };

  const getPlanValidityToggle = () => {
    const currency = Object.keys(plans);
    return (
      <Stack direction="row">
        {currency?.includes("USD") && (
          <Box
            sx={[
              classes.toggleButton,
              planCurrency === "USD" && classes.activeButton,
            ]}
            onClick={() => setPlanCurrency("USD")}
          >
            <Typography variant="body2" px={3}>
              USD
            </Typography>
          </Box>
        )}
        {currency.includes("INR") && (
          <Box
            sx={[
              classes.toggleButton,
              planCurrency === "INR" && classes.activeButton,
            ]}
            onClick={() => setPlanCurrency("INR")}
          >
            <Typography variant="body2" px={3}>
              INR
            </Typography>
          </Box>
        )}
      </Stack>
    );
  };

  const getUpgradePlanHeader = () => {
    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {isTruthy(allPlans) && getPlanValidityToggle()}
        {isAnnualPlans && (
          <Stack direction="row" alignItems="center">
            <Typography
              variant="body2"
              fontWeight={600}
              pr={1}
              color={planType === "yearly" ? "#7551D0" : "text.primary"}
            >
              Billed yearly
            </Typography>
            <CustomSwitch
              checked={planType === "yearly"}
              onChange={handlePlanTypeToggle}
            />
            <Typography
              variant="body2"
              fontWeight={600}
              pl={1}
              color={planType === "yearly" ? "#7551D0" : "text.primary"}
            >
              (10% in yearly plan)
            </Typography>
          </Stack>
        )}
      </Stack>
    );
  };

  const getUpgradePlan = (plan: any) => {
    return (
      <Grid item xs={12} md={4}>
        <Card sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={classes.planPriceBox}>
            <Stack direction="row" spacing={1}>
              <Stack direction="row">
                <Typography variant="caption" sx={classes.planPrice}>
                  {plan.currency === "INR"
                    ? `₹${getFormattedStatsCount(plan?.price ?? 0)}`
                    : `$${getFormattedStatsCount(plan?.price ?? 0)}`}
                </Typography>
              </Stack>
              <Typography sx={classes.planPriceText} variant="caption">
                / month
              </Typography>
            </Stack>
          </Box>
          <Stack p={3} pb={0} flexGrow={1}>
            <Stack direction="column" mb={2}>
              <Typography variant="caption" sx={classes.planName} pb={2}>
                {plan.name}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {plan.limits.map((item: planLimits) => (
                <Stack pl={3} pb={1} spacing={2}>
                  <List sx={{ listStyleType: "disc", p: "0px !important" }}>
                    <ListItem
                      sx={{ display: "list-item", p: "0px !important" }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {`${getFormattedStatsCount(
                          item.limit ?? 0
                        )} ${capitalizeEntity(item.entity)}`}
                      </Typography>
                    </ListItem>
                  </List>
                </Stack>
              ))}
            </Stack>
            <Stack>
              {features
                .filter(
                  (category) =>
                    category.title === "Email" ||
                    category.title === "Marketing CRM"
                )
                .map((category, index) => (
                  <Box key={index} style={{ marginBottom: "16px" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, marginBottom: 1 }}
                    >
                      {category.title}
                    </Typography>
                    {category.items.map((item, i) => (
                      <Typography
                        key={i}
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          marginBottom: 0.5,
                        }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                ))}
            </Stack>
          </Stack>
          <Box
            display="flex"
            justifyContent="center"
            sx={{ mt: "auto", mb: 2 }}
          >
            <Button
              variant="contained"
              disabled={activePlanId === plan.id}
              onClick={() => {
                setUpgradePlan(true);
                setSelectedPlan(plan);
              }}
            >
              {activePlanId === plan.id ? "Your Current Plan" : "Choose Plan"}
            </Button>
          </Box>
        </Card>
      </Grid>
    );
  };

  const getSkeleton = () => {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3].map((_, index) => (
          <Grid item xs={12} sm={4} key={index}>
            <Skeleton animation="wave" variant="rounded" height={700} />
          </Grid>
        ))}
      </Grid>
    );
  };
  const getUpgradePlans = () => {
    return (
      <Stack direction="column" spacing={2}>
        {getUpgradePlanHeader()}
        {isTruthy(allPlans) && (
          <Box>
            <Grid container spacing={3} alignItems="stretch">
              {plans[planCurrency]?.map((plan: any) => getUpgradePlan(plan))}
            </Grid>
          </Box>
        )}
      </Stack>
    );
  };

  return <>{isLoading ? getSkeleton() : getUpgradePlans()}</>;
};

export default UpgradePlan;
